import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useRemiStore } from "../RemiContext";

/**
 * Builds the PageContext and SessionContext objects ChatMS expects.
 *
 * Field names and types match ChatMS/ChatMS/docs/Chat.md exactly. Two things that
 * will 400 or silently degrade the answer if you get them wrong:
 *   - sDOS must be MM/dd/yyyy. Anything else is rejected with 400, so we omit
 *     rather than send a value we couldn't parse.
 *   - SessionContext drives the claim-status row filter. Omit it and ChatMS gets a
 *     thinner result set than the screen shows, then honestly reports "no claims
 *     found" - which reads to the biller as the bot being broken.
 *
 * Sources, in increasing priority:
 *   1. the route            (ids that live in the URL)
 *   2. selectedClaimItem    (the row the biller clicked, already in localStorage)
 *   3. Redux state.user.claimId
 *   4. whatever a screen published via usePublishPageContext()
 */

/**
 * Route -> screen name. Every pattern here must match a path that actually exists in App.js.
 *
 * These are not cosmetic. The name resolved here gates SCREENS_WITH_SELECTED_ROW below, which
 * decides whether the claim the biller clicked is read at all — so a pattern that matches
 * nothing costs the screen its claim id, its context chip and its screen-specific prompts,
 * silently. That is what "Removed the V2 from Billing" did: the routes became /ClaimStatus,
 * /claim-approval, /posting-payments and /Lab-Billing while this table still looked for
 * /ClaimStatusV2, /Claimapproval, /PostingPayments and /LabBilling, so Remi ran context-blind
 * on four of the busiest billing screens and nothing failed loudly enough to notice.
 *
 * If you rename a route, change it here in the same commit.
 */
const SCREENS = [
  [/^\/ClaimStatus/i, "Claim Status"],
  [/^\/DenialManagement/i, "Denial Management"],
  [/^\/workableclaims/i, "Workable Claims"],
  [/^\/posting-payments/i, "Posting Payments"],
  [/^\/BillingdasboardV2/i, "Billing Dashboard V2"],
  [/^\/Billingdasboard/i, "Billing Dashboard"],
  [/^\/IntelliBillV2/i, "IntelliBill V2"],
  [/^\/Intellibill/i, "IntelliBill"],
  [/^\/claim-approval/i, "Claim Approval"],
  [/^\/Lab-Billing/i, "Lab Billing"],
  [/^\/new-encounter-v2/i, "Encounter V2"],
  [/^\/new-encounter/i, "Encounter"],
  [/^\/Upload-RemittanceFilesv2/i, "Remittance V2"],
  [/^\/Upload-RemittanceFiles/i, "Remittance"],
  [/^\/SuperBillv2/i, "Superbill V2"],
  [/^\/SuperBill/i, "Superbill"],
  [/^\/RHMAgingRecievedCharges/i, "AR Aging Report"],
  [/^\/ARAgingReportClaimDetails/i, "AR Aging Claim Details"],
  [/^\/InsurancePaymentReport/i, "Insurance Payment Report"],
  [/^\/CptReport/i, "CPT Report"],
  [/^\/UpdateCPT/i, "CPT Fee Schedule"],
  [/^\/transactionlogs/i, "Transaction Logs"],
  [/^\/patient-dashboard/i, "Patient Dashboard"],
  [/^\/patientdashboard/i, "Patient Dashboard"],
  [/^\/patient-insurance/i, "Patient Insurance"],
  [/^\/PatientContacts/i, "Patient Contacts"],
  [/^\/PatientStatementsWorkflow/i, "Patient Statements"],
  [/^\/BillerPatientTrackerV2/i, "Patient Tracker V2"],
  [/^\/BillerPatientTracker/i, "Patient Tracker"],
  [/^\/BillerDashboard/i, "Biller Dashboard"],
  [/^\/BillerPatients/i, "Biller Patients"],
  [/^\/BillerClaimApproval/i, "Biller Claim Approval"],
  [/^\/BillerInsuranceVerification/i, "Insurance Verification"],
  [/^\/BillerPatientDetail/i, "Patient Detail"],
  [/^\/BillerClaimReview/i, "Claim Review"],
  [/^\/BillerClaimPosting/i, "Claim Posting"],
  [/^\/BillerResubmit/i, "Resubmit"],
  [/^\/MedicalClaimMacro/i, "Claim Macros"],
  [/^\/GeneralDocuments/i, "Documents"],
  [/^\/Fax/i, "Fax"],
  [/^\/locations/i, "Location Select"],
];

const PATIENT_ID_ROUTES = [
  /^\/patient-dashboard\/([^/]+)/i,
  /^\/patientdashboard\/([^/]+)/i,
  /^\/patient-insurance\/([^/]+)/i,
  /^\/PatientContacts\/([^/]+)/i,
  /^\/GeneralDocuments\/([^/]+)/i,
  /^\/BillerPatientDetail\/([^/]+)/i,
  /^\/BillerClaimApproval\/([^/]+)/i,
];

const CLAIM_ID_ROUTES = [/^\/transactionlogs\/([^/]+)/i];

function firstCapture(pathname, patterns) {
  for (const p of patterns) {
    const m = pathname.match(p);
    if (m && m[1]) return decodeURIComponent(m[1]);
  }
  return null;
}

/** ChatMS wants numeric ids. Anything non-numeric is omitted rather than sent as junk. */
function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).trim());
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * ChatMS rejects anything that isn't MM/dd/yyyy with a 400, so omit on doubt.
 *
 * A date of service is a calendar date, not an instant, so it must never go through
 * `new Date(...)` + local getters. Date-only ISO strings parse as UTC midnight while
 * getDate()/getMonth()/getFullYear() read local time, so west of UTC every one of them
 * shifts back a day — and "2026-01-01" comes out as 12/31/2025, the wrong year. Remi
 * would then query the wrong date, find nothing, and truthfully report no claims, which
 * reads to the biller as the assistant being broken.
 *
 * So: pull the components out textually, and only fall back to Date for values that
 * genuinely carry a time (where local parsing is what we want).
 */
function toMMDDYYYY(value) {
  if (!value) return null;

  if (typeof value === "string") {
    const s = value.trim();

    // Already MM/dd/yyyy.
    const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (us) return `${us[1].padStart(2, "0")}/${us[2].padStart(2, "0")}/${us[3]}`;

    // ISO yyyy-MM-dd, with or without a trailing time. Read the date part verbatim.
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

/**
 * The portal stores DefaultTimeZoneOffSet as JS getTimezoneOffset() minutes
 * (positive = behind UTC, e.g. 300 for US Eastern). ChatMS wants "-05:00".
 */
function toOffsetString(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "string" && /^[+-]\d{2}:\d{2}$/.test(raw.trim())) return raw.trim();

  const minutes = Number(raw);
  if (!Number.isFinite(minutes)) return null;

  const sign = minutes > 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}

/** The user object uses RoleID / RoleId / roleID interchangeably across the codebase. */
function pick(obj, ...keys) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== null && v !== undefined && v !== "") return v;
  }
  return null;
}

/**
 * Screens that write `selectedClaimItem` when a biller clicks a row — so on those screens the
 * stored row genuinely reflects what is on the page.
 *
 * Everywhere else it is a leftover. localStorage is never cleared, so a claim clicked this
 * morning is still sitting there this afternoon, and without this gate Remi would report
 * "Claim 84213" while the biller is looking at an AR report — then answer questions about it
 * with complete confidence. Stale context is worse than no context, because nothing about the
 * answer looks wrong.
 *
 * Matched against the resolved screen name, so it stays in step with the SCREENS table above
 * rather than duplicating its route patterns.
 */
const SCREENS_WITH_SELECTED_ROW = new Set([
  "Claim Status",
  "Claim Status V2",
  "Denial Management",
  "Workable Claims",
  "Posting Payments",
  "Posting Payments V2",
  "IntelliBill",
  "IntelliBill V2",
  "Claim Approval",
  "Claim Approval V2",
  "Biller Claim Approval",
  "Biller Claim Review",
  "Claim Posting",
  "Transaction Logs",
  "Superbill",
  "Superbill V2",
  "CPT Report",
  "Remittance",
  "Remittance V2",
  "Lab Billing",
  "Lab Billing V2",
]);

function readSelectedClaimRow(screenName) {
  if (!screenName || !SCREENS_WITH_SELECTED_ROW.has(screenName)) return null;

  try {
    const raw = localStorage.getItem("selectedClaimItem");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function stripEmpty(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== "")
  );
}

export default function usePageContext() {
  const { pathname } = useLocation();
  const userData = useSelector((state) => state.user.user);
  const reduxClaimId = useSelector((state) => state.user.claimId);
  const { published } = useRemiStore();

  return useMemo(() => {
    const screen = SCREENS.find(([re]) => re.test(pathname));
    const screenName = screen ? screen[1] : null;

    // Two of the four sources are "sticky" — they are written when a biller picks a record and
    // never cleared afterwards, so away from a record screen they describe the past, not the
    // page. Trust them only where a selected claim actually means something.
    //
    //   published        cleared on unmount, always current
    //   route capture    read from the URL, always current
    //   reduxClaimId     set by SET_ID, no clearing action exists  -> gated
    //   selectedClaimItem  written to localStorage, never removed  -> gated
    const bRecordScreen = screenName !== null && SCREENS_WITH_SELECTED_ROW.has(screenName);
    const row = bRecordScreen ? readSelectedClaimRow(screenName) : null;
    const stickyClaimId = bRecordScreen ? reduxClaimId : null;

    const pageContext = stripEmpty({
      sRoute: pathname,
      sScreenName: screenName,
      ClaimID:
        toNumber(published.ClaimID) ??
        toNumber(firstCapture(pathname, CLAIM_ID_ROUTES)) ??
        toNumber(stickyClaimId) ??
        toNumber(pick(row, "ClaimID", "claimID", "claimId")),
      PatientID:
        toNumber(published.PatientID) ??
        toNumber(firstCapture(pathname, PATIENT_ID_ROUTES)) ??
        toNumber(pick(row, "PatientID", "patientID", "patientId")),
      sMRN: published.sMRN ?? pick(row, "MRN", "mrn", "sMRN") ?? null,
      sDOS: toMMDDYYYY(published.sDOS ?? pick(row, "DOS", "dos", "sDOS")),
    });

    const sessionContext = stripEmpty({
      // 7 is Billin_Opr_7 — Billing Operator — in CommonEnums.ApplicationRole on the backend.
      // It is the correct default for a billing-portal user and matches what Login.js and the
      // remittance and superbill screens already send. Not arbitrary: iRoleID reaches the
      // claim-status row filter, so a wrong value would quietly change which claims Remi sees
      // compared with what is on the biller's screen.
      iRoleID: toNumber(pick(userData, "RoleID", "RoleId", "roleID", "roleId")) ?? 7,
      iProviderTemplateID: toNumber(pick(userData, "iProviderTemplateID", "ProviderTemplateID")) ?? 0,
      sDefaultTimeZoneOffSet: toOffsetString(pick(userData, "DefaultTimeZoneOffSet", "sDefaultTimeZoneOffSet")),
      sLocationID: String(
        pick(userData, "LocationID", "LocationId", "locationID", "locationId") ??
          localStorage.getItem("selectedLocation") ??
          ""
      ),
    });

    return {
      practiceID: toNumber(pick(userData, "practiceID", "PracticeID", "practiceId")),
      userID: toNumber(pick(userData, "userID", "UserID", "userId")),
      pageContext,
      sessionContext,
    };
  }, [pathname, userData, reduxClaimId, published]);
}
